import React, { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import EmptyState from '@dashboard/components/EmptyState';
import { subscriptionEmailLogsKey } from '@dashboard/lib/queryKeys';
import { DataGrid } from '@dashboard/components/DataGrid';
import { getEmailLogColumns } from '@dashboard/components/EmailLogColumns';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { isBefore } from 'date-fns';
import { confirm } from '@/dashboard/lib/confirm';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import { Button } from '@dashboard/components/ui/button';
import { toast } from '@/dashboard/context/ToastContext';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';

const SubscriptionsDetailEmailLogs = ({ subscription }) => {
  const { sellerId, token } = useAuth();

  const {
    data: emailLogs,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: subscriptionEmailLogsKey(subscription._id),
    queryFn: () =>
      fetcher(`/sellers/${sellerId}/email-logs?subscriptionId=${subscription._id}`, token),
    enabled: !!subscription?._id,
  });

  const {
    mutate: sendSubscriptionConfirmationEmail,
    isPending: isSendingSubscriptionConfirmationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${sellerId}/subscriptions/${subscription._id}/send-confirmation-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      refetch();
      toast({
        title: 'Confirmation email resent',
      });
    },
    onError: error => {
      refetch();
      toast({
        title: 'Failed to send confirmation email',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function handleSendSubscriptionConfirmationEmail() {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubscriptionConfirmationEmail();
    }
  }

  const {
    mutate: sendSubscriptionNotificationEmail,
    isPending: isSendingSubscriptionNotificationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${sellerId}/subscriptions/${subscription._id}/send-notification-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      refetch();
      toast({
        title: 'Notification email resent',
      });
    },
    onError: error => {
      refetch();
      toast({
        title: 'Failed to send notification email',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function handleSendSubscriptionNotificationEmail() {
    const confirmed = await confirm('Send a new notification email to you and your team?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubscriptionNotificationEmail();
    }
  }

  const columns = useMemo(() => getEmailLogColumns(), []);
  const isLegacyUntrackedPeriod = isBefore(
    new Date(subscription.createdAt),
    new Date('2024-12-17'),
  );
  const emptyState = (
    <EmptyState>
      <EmptyState.Subtitle>
        {isLegacyUntrackedPeriod
          ? 'Email tracking unavailable before December 17, 2024'
          : 'No emails found for this subscription.'}
      </EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4 pt-2">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader className="mb-4 gap-2">
            <CardHeaderTitle>Emails</CardHeaderTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendSubscriptionConfirmationEmail}
                disabled={
                  isSendingSubscriptionConfirmationEmail ||
                  isAbandonedAbandonment(subscription) ||
                  subscription.status === 'failed' ||
                  subscription.status === 'incomplete'
                }
              >
                Resend confirmation
                {isSendingSubscriptionConfirmationEmail ? <Spinner /> : <ArrowPathIcon />}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleSendSubscriptionNotificationEmail}
                disabled={
                  isSendingSubscriptionNotificationEmail ||
                  isAbandonedAbandonment(subscription) ||
                  subscription.status === 'failed' ||
                  subscription.status === 'incomplete'
                }
              >
                Resend notification
                {isSendingSubscriptionNotificationEmail ? <Spinner /> : <ArrowPathIcon />}
              </Button>
              <EmailCustomerDialog
                asButton
                customerEmail={subscription.customer?.email}
                livemode={subscription.livemode}
                objectLabel="subscriptions"
                endpoint={`/sellers/${sellerId}/subscriptions/${subscription._id}/send-email`}
                variables={buildEmailVariables({ page: subscription.page })}
                queryKey={subscriptionEmailLogsKey(subscription._id)}
                emailsPath={`/subscriptions/${subscription._id}/emails`}
              />
            </div>
          </CardHeader>

          <CardContent>
            {isError ? (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertDescription>Failed to load emails</AlertDescription>
              </Alert>
            ) : (
              <DataGrid
                columns={columns}
                data={emailLogs ?? []}
                isLoading={isLoading}
                emptyState={emptyState}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionsDetailEmailLogs;
